import { type Monitor, Signal, batch } from "@suse-o11y/signal";

import type { Time } from "~/tools/date-time/time";
import { toArray } from "~/tools/to-array";

interface TimestampCursorValue {
	sourceId: string;
	timestamp: number;
}

export interface TimestampCursorMarker {
	timestamp: number;
	isHighlighted: boolean;
}

export class TimestampCursorState {
	/**
	 * The `cursor` {@link Signal} keeps track of the current cursor position and source.
	 * It allows to synchronize the cursor position between multiple sources.
	 */
	private readonly cursor = Signal<TimestampCursorValue>();

	/**
	 * The `markers` {@link Signal} keeps track of all the timestamps that have been marked.
	 * It allows to show the markers on multiple consumers (charts, timeline, etc).
	 */
	private readonly markers = Signal<ReadonlySet<number>>(new Set<number>());

	/**
	 * The `hotkeyPressed` {@link Signal} keeps track of whether the hotkey is currently pressed.
	 */
	private readonly hotkeyPressed = Signal(false);

	/**
	 * A marker that is currently hovered by the user.
	 * It plays a role in highlighting a marker when the hotkey is pressed.
	 */
	private readonly hoveredMarker = Signal<number>();

	/**
	 * A marker that is currently banned from being highlighted.
	 * It is necessary to avoid highlighting a just toggled marker.
	 */
	private readonly bannedHighlightMarker = Signal<number>();

	public isHotkeyPressed(monitor: Monitor): boolean {
		return this.hotkeyPressed.read(monitor);
	}

	public setHotkeyPressed(isPressed: boolean): void {
		batch(() => {
			this.hotkeyPressed.write(isPressed);

			if (!isPressed) {
				this.bannedHighlightMarker.write(undefined);
			}
		});
	}

	public getCursor(monitor: Monitor, sourceId: string): undefined | number {
		const cursor = this.cursor.read(monitor);

		if (cursor == null || cursor.sourceId === sourceId) {
			return undefined;
		}

		// don't display the cursor if any marker is highlighted or the cursor is already marked
		if (this.getHighlightedMarker(monitor) != null || this.markers.read(monitor).has(cursor.timestamp)) {
			return undefined;
		}

		return cursor.timestamp;
	}

	public setCursor(time: Time, sourceId: string): void {
		this.cursor.write({
			sourceId,
			timestamp: time.valueOf(),
		});
	}

	public resetCursor(): void {
		this.cursor.write(undefined);
	}

	public getHighlightedMarker(monitor: Monitor): undefined | number {
		// marker is not hovered if hotkey is not pressed
		if (!this.isHotkeyPressed(monitor)) {
			return undefined;
		}

		const hoveredMarker = this.hoveredMarker.read(monitor);

		if (
			hoveredMarker != null &&
			hoveredMarker !== this.bannedHighlightMarker.read(monitor) &&
			this.markers.read(monitor).has(hoveredMarker)
		) {
			return hoveredMarker;
		}

		return undefined;
	}

	public setHighlightedMarker(time: undefined | Time): void {
		const timestamp = time?.valueOf();

		batch((monitor) => {
			if (timestamp == null || this.markers.read(monitor).has(timestamp)) {
				this.hoveredMarker.write(timestamp);
			}

			if (timestamp == null) {
				this.bannedHighlightMarker.write(undefined);
			}
		});
	}

	public getAllMarkers(monitor: Monitor): Array<TimestampCursorMarker> {
		const highlightedMarker = this.getHighlightedMarker(monitor);

		return toArray(this.markers.read(monitor), (timestamp) => ({
			timestamp,
			isHighlighted: timestamp === highlightedMarker,
		}));
	}

	public toggleMarker(time: Time): void {
		batch(() => {
			const timestamp = time.valueOf();

			this.markers.write((current) => {
				const clone = new Set(current);

				if (clone.has(timestamp)) {
					clone.delete(timestamp);
				} else {
					clone.add(timestamp);
				}

				return clone;
			});

			this.bannedHighlightMarker.write(timestamp);
		});
	}
}
